"use client";

import { useMutation } from "@tanstack/react-query";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/locales/client";
import { useTRPC } from "@/trpc/client";

interface AlternativeFeedbackStepProps {
  onBack: () => void;
  onFeedbackSubmitted: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export const AlternativeFeedbackStep = ({
  onBack,
  onFeedbackSubmitted,
  onSubmit,
  isSubmitting,
}: AlternativeFeedbackStepProps) => {
  const [alternative, setAlternative] = useState("");
  const [reason, setReason] = useState("");
  const t = useI18n();
  const trpc = useTRPC();

  const submitFeedback = useMutation(
    trpc.cancellation.submitCancellationFeedback.mutationOptions({
      onSuccess: () => {
        onFeedbackSubmitted();
        onSubmit();
      },
      onError: (error) => {
        toast.error(
          error.message || t("feedbackModal.feedbackForm.submitError"),
        );
      },
    }),
  );

  const handleSubmit = () => {
    if (!alternative.trim()) return;
    submitFeedback.mutate({
      reason: "found_alternative",
      outcome: "canceled",
      alternativeTool: alternative.trim(),
      alternativeReason: reason.trim() || undefined,
    });
  };

  const isPending = submitFeedback.isPending || isSubmitting;
  const canSubmit = alternative.trim().length > 0 && !isPending;

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-muted-foreground">
        {t("cancellation.alternativeFeedback.description")}
      </p>

      <div>
        <label
          htmlFor="cancellation-alternative"
          className="mb-1.5 block text-sm font-medium text-foreground"
        >
          {t("cancellation.alternativeFeedback.alternativeLabel")}{" "}
          <span className="text-red-500">*</span>
        </label>
        <Textarea
          id="cancellation-alternative"
          placeholder={t(
            "cancellation.alternativeFeedback.alternativePlaceholder",
          )}
          value={alternative}
          onChange={(e) => setAlternative(e.target.value)}
          className="resize-vertical focus-visible:ring-brand-primary"
          rows={2}
        />
      </div>

      <div>
        <label
          htmlFor="cancellation-alternative-reason"
          className="mb-1.5 block text-sm font-medium text-foreground"
        >
          {t("cancellation.alternativeFeedback.reasonLabel")}
        </label>
        <Textarea
          id="cancellation-alternative-reason"
          placeholder={t("cancellation.alternativeFeedback.reasonPlaceholder")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="resize-vertical focus-visible:ring-brand-primary"
          rows={3}
        />
      </div>

      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="gap-1">
          <ChevronLeft className="size-4" />
          {t("cancellation.reason.goBack")}
        </Button>
        <Button
          variant="dangerOutline"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
          {t("cancellation.offers.continueCanceling")}
        </Button>
      </div>
    </div>
  );
};
